/**
 * The one canonical chart resolution set.
 *
 * Before this existed the app carried four incompatible spellings of the same
 * timeframe: `SupportedResolutionEnum.DAY = "1D"` (useDatafeed.helpers), the
 * legacy `TV_INTERVAL["1d"] = "D"` map (tvOverrides), Binance's `"1d"`, and a
 * `PERPS_CHART_INTERVAL` localStorage value of `"15m"`. Anything crossing those
 * boundaries had to guess.
 *
 * These ids are the ones the backend accepts verbatim on `GET /candles` and on
 * the `candles:{instrument}:{resolution}` websocket channel. The backend
 * rejects anything else with a 400 rather than substituting a timeframe, so
 * this list and its list must agree.
 *
 * The legacy tv.js path keeps its own mapping; nothing here changes it.
 */

export interface ChartResolution {
  /** Sent to the API. */
  id: string;
  /** Shown on the interval selector. */
  label: string;
  /** Bar length in seconds, or null for calendar months. */
  seconds: number | null;
}

export const CHART_RESOLUTIONS: ChartResolution[] = [
  { id: "1", label: "1m", seconds: 60 },
  { id: "5", label: "5m", seconds: 300 },
  { id: "15", label: "15m", seconds: 900 },
  { id: "30", label: "30m", seconds: 1800 },
  { id: "60", label: "1h", seconds: 3600 },
  { id: "240", label: "4h", seconds: 14400 },
  { id: "1D", label: "1D", seconds: 86400 },
  { id: "1W", label: "1W", seconds: 604800 },
  // Calendar months vary in length; the backend aligns them to the 1st UTC.
  { id: "1M", label: "1M", seconds: null },
];

export const DEFAULT_CHART_RESOLUTION = "15";

/** localStorage key holding the selected resolution id. */
export const CHART_RESOLUTION_STORAGE_KEY = "NATIVE_CHART_RESOLUTION";

const BY_ID = new Map(CHART_RESOLUTIONS.map((r) => [r.id, r]));

/**
 * Legacy `PERPS_CHART_INTERVAL` values, so a user who last used the tv.js or
 * Binance chart keeps their timeframe on the first native render instead of
 * silently landing back on the default.
 */
const LEGACY_ALIASES: Record<string, string> = {
  "1m": "1",
  "5m": "5",
  "15m": "15",
  "30m": "30",
  "1h": "60",
  "4h": "240",
  "1d": "1D",
  D: "1D",
  "1w": "1W",
  W: "1W",
  M: "1M",
};

/** Resolve a stored or user-supplied value to a supported resolution id. */
export const toChartResolution = (raw: string | null | undefined): string => {
  if (!raw) return DEFAULT_CHART_RESOLUTION;
  const text = raw.trim();
  if (BY_ID.has(text)) return text;
  return LEGACY_ALIASES[text] ?? DEFAULT_CHART_RESOLUTION;
};

export const getChartResolution = (id: string): ChartResolution | undefined =>
  BY_ID.get(id);

/** The stored resolution, falling back to the default when unreadable. */
export const readStoredResolution = (): string => {
  try {
    return toChartResolution(
      localStorage.getItem(CHART_RESOLUTION_STORAGE_KEY) ??
        // The tv.js/Binance charts wrote here first.
        localStorage.getItem("PERPS_CHART_INTERVAL")
    );
  } catch {
    return DEFAULT_CHART_RESOLUTION;
  }
};

export const storeResolution = (id: string): void => {
  try {
    localStorage.setItem(CHART_RESOLUTION_STORAGE_KEY, id);
  } catch {
    /* private mode / blocked storage — the choice just does not persist */
  }
};
